import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaTrash,
  FaChartLine,
  FaSpinner,
  FaTrophy,
  FaCheckCircle,
  FaTimesCircle,
  FaUserGraduate,
  FaDownload,
  FaShare,
  FaBook,
  FaCalendarAlt,
  FaUniversity,
  FaStar,
  FaCrown,
  FaMedal,
  FaEye,
  FaShieldAlt,
  FaRocket,
  FaArrowLeft,
  FaLink,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "axios";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";

const ResultComparison = () => {
  const [usns, setUsns] = useState(["", ""]);
  const [semester, setSemester] = useState("all");
  const [examType, setExamType] = useState("Regular");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compared, setCompared] = useState(false);
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const semesters = ["all", 1, 2, 3, 4, 5, 6, 7, 8];

  const handleUsnChange = (index, value) => {
    const newUsns = [...usns];
    newUsns[index] = value.toUpperCase();
    setUsns(newUsns);
  };

  const handleAddUsn = () => {
    if (usns.length < 5) {
      setUsns([...usns, ""]);
    } else {
      toast.error("Maximum 5 USNs can be compared");
    }
  };

  const handleRemoveUsn = (index) => {
    if (usns.length > 2) {
      const newUsns = usns.filter((_, i) => i !== index);
      setUsns(newUsns);
    } else {
      toast.error("At least 2 USNs required for comparison");
    }
  };

  const handleCompare = async () => {
    const validUsns = usns.filter((usn) => usn.trim().length > 0);
    if (validUsns.length < 2) {
      toast.error("Please enter at least 2 valid USNs");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        "/api/results/compare-live",
        {
          usns: validUsns,
          semester: semester === "all" ? null : semester,
          examType,
        },
      );
      setResults(response.data.results);
      setCompared(true);
      toast.success("Comparison completed!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to compare results");
    } finally {
      setLoading(false);
    }
  };

  const downloadComparison = async () => {
    const element = document.getElementById("comparison-results");
    if (element) {
      try {
        const canvas = await html2canvas(element);
        const link = document.createElement("a");
        link.download = "result-comparison.png";
        link.href = canvas.toDataURL();
        link.click();
        toast.success("Downloaded as image!");
      } catch (error) {
        toast.error("Failed to download");
      }
    }
  };

  const shareComparison = () => {
    const text = results
      .map((r) => `${r.name} (${r.usn}): ${r.totalMarks} marks`)
      .join("\n");
    if (navigator.share) {
      navigator.share({ title: "Result Comparison", text });
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    }
  };

  const sortedResults = [...results].sort(
    (a, b) => (b.totalMarks || 0) - (a.totalMarks || 0),
  );

  const getRankBadge = (index) => {
    if (index === 0)
      return {
        bg: "linear-gradient(135deg, #f59e0b, #d97706)",
        color: "white",
        icon: <FaCrown />,
        label: "1st",
      };
    if (index === 1)
      return {
        bg: "linear-gradient(135deg, #94a3b8, #64748b)",
        color: "white",
        icon: <FaMedal />,
        label: "2nd",
      };
    if (index === 2)
      return {
        bg: "linear-gradient(135deg, #cd7f32, #b87333)",
        color: "white",
        icon: <FaMedal />,
        label: "3rd",
      };
    return {
      bg: isDarkMode ? "rgba(139,92,246,0.2)" : "#e2e8f0",
      color: getTextColor(),
      icon: <FaTrophy />,
      label: `${index + 1}th`,
    };
  };

  const toggleExpand = (usn) => {
    setExpandedStudent(expandedStudent === usn ? null : usn);
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const tableRowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.08,
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    }),
  };

  const expandVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: "auto",
      transition: { duration: 0.3, ease: "easeInOut" },
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.2, ease: "easeInOut" },
    },
  };

  return (
    <div style={{ position: "relative" }}>
      {/* Input Section - Glassmorphism */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          padding: "1.5rem",
          border: `1px solid ${getBorderColor()}`,
          marginBottom: "1.5rem",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginBottom: "1rem",
          }}
        >
          <div
            style={{
              width: "32px",
              height: "32px",
              background: "rgba(139,92,246,0.1)",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaUserGraduate size={14} color="#8b5cf6" />
          </div>
          <h3
            style={{
              fontSize: "1rem",
              fontWeight: "600",
              color: getTextColor(),
              margin: 0,
            }}
          >
            Compare Results
          </h3>
        </div>

        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
        >
          {usns.map((usn, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              style={{ display: "flex", gap: "0.5rem" }}
            >
              <input
                type="text"
                value={usn}
                onChange={(e) => handleUsnChange(index, e.target.value)}
                placeholder={`USN ${index + 1}`}
                style={{
                  flex: 1,
                  padding: "0.6rem 0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  fontSize: "0.85rem",
                  outline: "none",
                  fontFamily: "monospace",
                  background: getInputBackground(),
                  color: getTextColor(),
                  transition: "all 0.3s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
              />
              {usns.length > 2 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleRemoveUsn(index)}
                  style={{
                    padding: "0 0.8rem",
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  <FaTrash size={12} />
                </motion.button>
              )}
            </motion.div>
          ))}
        </div>

        <motion.button
          whileHover={{ scale: 1.02, x: 5 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleAddUsn}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.3rem",
            marginTop: "0.75rem",
            padding: "0.3rem 0.8rem",
            background: "none",
            border: `2px dashed #8b5cf6`,
            borderRadius: "8px",
            color: "#8b5cf6",
            fontSize: "0.75rem",
            cursor: "pointer",
          }}
        >
          <FaPlus size={10} /> Add Another USN
        </motion.button>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "0.75rem",
            margin: "1rem 0",
          }}
        >
          <select
            value={semester}
            onChange={(e) => setSemester(e.target.value)}
            style={{
              padding: "0.5rem",
              border: `2px solid ${getBorderColor()}`,
              borderRadius: "8px",
              fontSize: "0.8rem",
              background: getInputBackground(),
              color: getTextColor(),
            }}
          >
            {semesters.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "All Semesters" : `Semester ${s}`}
              </option>
            ))}
          </select>
          <select
            value={examType}
            onChange={(e) => setExamType(e.target.value)}
            style={{
              padding: "0.5rem",
              border: `2px solid ${getBorderColor()}`,
              borderRadius: "8px",
              fontSize: "0.8rem",
              background: getInputBackground(),
              color: getTextColor(),
            }}
          >
            <option value="Regular">Regular</option>
            <option value="Revaluation">Revaluation</option>
          </select>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleCompare}
          disabled={loading}
          style={{
            width: "100%",
            padding: "0.7rem",
            background: loading
              ? "#94a3b8"
              : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            color: "white",
            border: "none",
            borderRadius: "12px",
            fontSize: "0.85rem",
            fontWeight: "600",
            cursor: loading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            boxShadow: loading ? "none" : "0 4px 14px 0 rgba(139,92,246,0.4)",
          }}
        >
          {loading ? (
            <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
          ) : (
            <FaChartLine />
          )}
          {loading ? "Comparing..." : "Compare Results"}
        </motion.button>
      </motion.div>

      {/* Results Section */}
      <AnimatePresence>
        {compared && results.length > 0 && (
          <motion.div
            id="comparison-results"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: 20 }}
          >
            {/* Action Buttons */}
            <motion.div
              variants={itemVariants}
              style={{
                display: "flex",
                gap: "0.5rem",
                justifyContent: "flex-end",
                marginBottom: "1rem",
              }}
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={downloadComparison}
                style={{
                  padding: "0.3rem 0.8rem",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.7rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                }}
              >
                <FaDownload size={10} /> Download
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={shareComparison}
                style={{
                  padding: "0.3rem 0.8rem",
                  background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.7rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                }}
              >
                <FaShare size={10} /> Share
              </motion.button>
            </motion.div>

            {/* Header */}
            <motion.div
              variants={itemVariants}
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                borderRadius: "14px",
                padding: "0.75rem 1rem",
                marginBottom: "1rem",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.5rem",
              }}
            >
              <div>
                <h4
                  style={{
                    margin: 0,
                    fontSize: "0.9rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                    color: "white",
                  }}
                >
                  <FaChartLine size={12} /> Result Comparison
                </h4>
                <p
                  style={{
                    fontSize: "0.7rem",
                    opacity: 0.9,
                    margin: "0.2rem 0 0",
                    color: "rgba(255,255,255,0.8)",
                  }}
                >
                  {examType} |{" "}
                  {semester === "all"
                    ? "All Semesters"
                    : `Semester ${semester}`}
                </p>
              </div>
              <div
                style={{
                  fontSize: "0.7rem",
                  opacity: 0.8,
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                {new Date().toLocaleString()}
              </div>
            </motion.div>

            {/* Leaderboard Style Table - Glassmorphism */}
            <motion.div
              variants={itemVariants}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "20px",
                overflow: "hidden",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr
                      style={{
                        background: isDarkMode
                          ? "rgba(139,92,246,0.05)"
                          : "#f8f9fa",
                        borderBottom: `2px solid ${getBorderColor()}`,
                      }}
                    >
                      <th
                        style={{
                          padding: "1rem",
                          textAlign: "center",
                          width: "70px",
                          color: getTextColor(),
                        }}
                      >
                        Rank
                      </th>
                      <th
                        style={{
                          padding: "1rem",
                          textAlign: "left",
                          color: getTextColor(),
                        }}
                      >
                        Student
                      </th>
                      <th
                        style={{
                          padding: "1rem",
                          textAlign: "center",
                          width: "100px",
                          color: getTextColor(),
                        }}
                      >
                        Total Marks
                      </th>
                      <th
                        style={{
                          padding: "1rem",
                          textAlign: "center",
                          width: "80px",
                          color: getTextColor(),
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: "1rem",
                          textAlign: "center",
                          width: "80px",
                          color: getTextColor(),
                        }}
                      >
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedResults.map((result, idx) => {
                      const rank = getRankBadge(idx);
                      const isExpanded = expandedStudent === result.usn;

                      return (
                        <React.Fragment key={result.usn}>
                          <motion.tr
                            custom={idx}
                            variants={tableRowVariants}
                            initial="hidden"
                            animate="visible"
                            style={{
                              borderBottom: `1px solid ${getBorderColor()}`,
                              background:
                                idx === 0
                                  ? isDarkMode
                                    ? "rgba(139,92,246,0.1)"
                                    : "rgba(139,92,246,0.05)"
                                  : "transparent",
                            }}
                            whileHover={{
                              background: isDarkMode
                                ? "rgba(139,92,246,0.08)"
                                : "rgba(139,92,246,0.03)",
                            }}
                          >
                            <td
                              style={{ padding: "1rem", textAlign: "center" }}
                            >
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "50%",
                                  background: rank.bg,
                                  color: rank.color,
                                  fontWeight: "bold",
                                }}
                              >
                                {rank.icon || rank.label}
                              </motion.div>
                            </td>
                            <td style={{ padding: "1rem" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.8rem",
                                }}
                              >
                                <motion.img
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{
                                    delay: idx * 0.05,
                                    type: "spring",
                                  }}
                                  src={`https://ui-avatars.com/api/?name=${result.name?.split(" ")[0] || "S"}&background=8b5cf6&color=fff&size=36&rounded=true`}
                                  alt={result.name}
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    borderRadius: "50%",
                                  }}
                                />
                                <div>
                                  <div
                                    style={{
                                      fontWeight: "600",
                                      fontSize: "0.9rem",
                                      color: getTextColor(),
                                    }}
                                  >
                                    {result.name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.7rem",
                                      color: getTextSecondary(),
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {result.usn}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td
                              style={{ padding: "1rem", textAlign: "center" }}
                            >
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{
                                  delay: idx * 0.05,
                                  type: "spring",
                                  stiffness: 400,
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "1.3rem",
                                    background:
                                      "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                  }}
                                >
                                  {result.totalMarks}
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.6rem",
                                    color: getTextSecondary(),
                                  }}
                                >
                                  out of {result.subjects?.length * 100 || 600}
                                </div>
                              </motion.div>
                            </td>
                            <td
                              style={{ padding: "1rem", textAlign: "center" }}
                            >
                              {result.resultStatus === "PASS" ? (
                                <motion.span
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{
                                    delay: idx * 0.05,
                                    type: "spring",
                                  }}
                                  style={{
                                    background: "rgba(16,185,129,0.1)",
                                    color: "#10b981",
                                    padding: "0.2rem 0.6rem",
                                    borderRadius: "20px",
                                    fontSize: "0.7rem",
                                    fontWeight: "500",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "0.3rem",
                                  }}
                                >
                                  <FaCheckCircle size={10} /> PASS
                                </motion.span>
                              ) : (
                                <motion.span
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{
                                    delay: idx * 0.05,
                                    type: "spring",
                                  }}
                                  style={{
                                    background: "rgba(239,68,68,0.1)",
                                    color: "#ef4444",
                                    padding: "0.2rem 0.6rem",
                                    borderRadius: "20px",
                                    fontSize: "0.7rem",
                                    fontWeight: "500",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "0.3rem",
                                  }}
                                >
                                  <FaTimesCircle size={10} /> FAIL
                                </motion.span>
                              )}
                            </td>
                            <td
                              style={{ padding: "1rem", textAlign: "center" }}
                            >
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => toggleExpand(result.usn)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#8b5cf6",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.3rem",
                                  fontSize: "0.7rem",
                                }}
                              >
                                <FaEye size={12} />{" "}
                                {isExpanded ? "Hide" : "View"}
                              </motion.button>
                            </td>
                          </motion.tr>
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.tr
                                variants={expandVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                              >
                                <td colSpan="5" style={{ padding: 0 }}>
                                  <div
                                    style={{
                                      padding: "1rem 1.5rem",
                                      background: isDarkMode
                                        ? "rgba(139,92,246,0.05)"
                                        : "#fafafa",
                                    }}
                                  >
                                    <div>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0.5rem",
                                          marginBottom: "0.8rem",
                                        }}
                                      >
                                        <FaBook size={12} color="#8b5cf6" />
                                        <span
                                          style={{
                                            fontSize: "0.75rem",
                                            fontWeight: "600",
                                            color: getTextColor(),
                                          }}
                                        >
                                          Subject-wise Marks
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "repeat(auto-fill, minmax(200px, 1fr))",
                                          gap: "0.5rem",
                                        }}
                                      >
                                        {result.subjects?.map((subject, i) => (
                                          <motion.div
                                            key={i}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: i * 0.03 }}
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                              padding: "0.5rem 0.8rem",
                                              background: getCardBackground(),
                                              borderRadius: "10px",
                                              border: `1px solid ${getBorderColor()}`,
                                            }}
                                          >
                                            <div>
                                              <span
                                                style={{
                                                  fontFamily: "monospace",
                                                  fontSize: "0.7rem",
                                                  fontWeight: "600",
                                                  color: getTextColor(),
                                                }}
                                              >
                                                {subject.subjectCode}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.6rem",
                                                  color: getTextSecondary(),
                                                  marginLeft: "0.3rem",
                                                }}
                                              >
                                                {subject.subjectName?.substring(
                                                  0,
                                                  20,
                                                )}
                                              </span>
                                            </div>
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "0.3rem",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontWeight: "bold",
                                                  fontSize: "0.8rem",
                                                  color:
                                                    subject.result === "P"
                                                      ? "#10b981"
                                                      : "#ef4444",
                                                }}
                                              >
                                                {subject.total}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.6rem",
                                                  color:
                                                    subject.result === "P"
                                                      ? "#10b981"
                                                      : "#ef4444",
                                                }}
                                              >
                                                ({subject.result})
                                              </span>
                                            </div>
                                          </motion.div>
                                        ))}
                                      </div>
                                      <div
                                        style={{
                                          marginTop: "0.8rem",
                                          display: "flex",
                                          gap: "1rem",
                                          justifyContent: "flex-end",
                                          fontSize: "0.7rem",
                                          color: getTextSecondary(),
                                        }}
                                      >
                                        <span>
                                          <FaBook size={12} /> Passed:{" "}
                                          <strong style={{ color: "#10b981" }}>
                                            {result.passedSubjects || 0}
                                          </strong>
                                        </span>
                                        <span>
                                          <FaTimesCircle size={12} /> Failed:{" "}
                                          <strong style={{ color: "#ef4444" }}>
                                            {result.failedSubjects || 0}
                                          </strong>
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </motion.tr>
                            )}
                          </AnimatePresence>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ResultComparison;

